import { isAgenticChatSessionThinking } from 'features/embeddedSidebar/chatHistory/agenticChatHistoryRowActivity'
import type { RecordedAgenticChatSession } from 'features/embeddedSidebar/hooks/useAgenticChatSessionHistory'
import type { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import { readStoredComposerDraftForSession } from 'features/knowledgeDocumentViewer/utils/agenticChatComposerDraft'
import { getAgenticConversationKey } from 'features/knowledgeDocumentViewer/utils/agenticConversationIdentity'
import type { RolodexEntryQueryBody } from 'features/rolodex'
import type { RolodexLoadedEntryJson } from 'types'

import {
  buildDocumentChatSessionLoadOptions,
  sessionsFromRolodexEntries,
} from './documentChatSessionRolodex'
import { DOCUMENT_CHAT_SESSION_PAGE_SIZE } from './documentChatSessionSchemas'

export type ChatHistoryBulkAction = 'archive' | 'delete' | 'unarchive'
export type ChatHistorySection = 'recent' | 'archived'

export interface ChatHistoryBulkActionHandlers {
  onArchiveSessions: (
    sessions: RecordedAgenticChatSession[]
  ) => Promise<boolean>
  onDeleteSessions: (sessionKeys: string[]) => Promise<boolean>
  onUnarchiveSessions: (
    sessions: RecordedAgenticChatSession[]
  ) => Promise<boolean>
  onRemoveLocalSession: (sessionKey: string) => void
}

export async function applyChatHistoryBulkAction({
  action,
  closeWorkspaceTab,
  getCurrentState,
  loadEntries,
  onArchiveSessions,
  onDeleteSessions,
  onUnarchiveSessions,
  onRemoveLocalSession,
  siteId,
  userId,
}: ChatHistoryBulkActionHandlers & {
  action: ChatHistoryBulkAction
  closeWorkspaceTab: (tabId: string) => boolean
  getCurrentState: () => {
    sessions: RecordedAgenticChatSession[]
    openTabs: readonly DocumentTab[]
  }
  loadEntries: (
    query: RolodexEntryQueryBody
  ) => Promise<RolodexLoadedEntryJson[]>
  siteId: string
  userId: string
}): Promise<boolean> {
  const loadedSessions = await loadBulkDocumentChatSessions({
    archived: action !== 'archive',
    loadEntries,
    siteId,
    userId,
  })
  const { sessions, openTabs } = getCurrentState()
  const toClear = selectBulkDocumentChatSessions({
    action,
    loadedSessions,
    currentSessions: sessions,
    siteId,
  }).filter(
    (session) =>
      action === 'unarchive' ||
      !readStoredComposerDraftForSession({
        conversationId: session.conversation.id,
        sessionKey: session.key,
        siteId,
      })
  )
  const tabsToClose = openTabs.filter((tab) =>
    toClear.some((session) => getAgenticConversationKey(tab) === session.key)
  )
  const persisted =
    action === 'archive'
      ? await onArchiveSessions(toClear)
      : action === 'unarchive'
        ? await onUnarchiveSessions(toClear)
        : await onDeleteSessions(toClear.map((session) => session.key))
  if (!persisted) return false
  if (action !== 'unarchive') {
    tabsToClose.forEach((tab) => closeWorkspaceTab(tab.id))
    toClear.forEach((session) => onRemoveLocalSession(session.key))
  }
  return true
}

export async function loadBulkDocumentChatSessions({
  archived,
  loadEntries,
  siteId,
  userId,
}: {
  archived: boolean
  loadEntries: (
    query: RolodexEntryQueryBody
  ) => Promise<RolodexLoadedEntryJson[]>
  siteId: string
  userId: string
}): Promise<RecordedAgenticChatSession[]> {
  const sessions: RecordedAgenticChatSession[] = []
  for (let offset = 0; ; offset += DOCUMENT_CHAT_SESSION_PAGE_SIZE) {
    const query = buildDocumentChatSessionLoadOptions({
      archived,
      offset,
      siteId,
      userId,
    })
    if (!query) return []
    const entries = await loadEntries({
      ...query,
      order_by: [
        { field: 'updated_at', descending: 'true' },
        { field: 'id', descending: 'true' },
      ],
    })
    sessions.push(...sessionsFromRolodexEntries(entries, siteId))
    if (entries.length < DOCUMENT_CHAT_SESSION_PAGE_SIZE) return sessions
  }
}

export function selectBulkDocumentChatSessions({
  action = 'archive',
  loadedSessions,
  currentSessions,
  siteId,
}: {
  action?: ChatHistoryBulkAction
  loadedSessions: RecordedAgenticChatSession[]
  currentSessions: RecordedAgenticChatSession[]
  siteId: string
}): RecordedAgenticChatSession[] {
  const sessionsByKey = new Map(
    loadedSessions.map((session) => [session.key, session])
  )
  const runningKeys = new Set(
    [...loadedSessions, ...currentSessions]
      .filter((session) => isAgenticChatSessionThinking(session))
      .map((session) => session.key)
  )
  currentSessions.forEach((session) => {
    const loaded = sessionsByKey.get(session.key)
    if (!loaded) return
    sessionsByKey.set(session.key, {
      ...loaded,
      ...session,
      archived: loaded.archived,
      pinned: session.pinned === true || loaded?.pinned === true,
      syncStatus: session.syncStatus ?? loaded?.syncStatus,
    })
  })
  return [...sessionsByKey.values()].filter(
    (session) =>
      session.siteId === siteId &&
      session.key.startsWith('power-doc-search-conversation') &&
      Boolean(session.archived) === (action !== 'archive') &&
      (action === 'unarchive' ||
        (!session.pinned &&
          !session.composerDraft &&
          !runningKeys.has(session.key))) &&
      (session.syncStatus === undefined ||
        session.syncStatus === 'idle' ||
        session.syncStatus === 'error')
  )
}
